import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@repo/ui/src/components/ui/sheet';
import { Button } from '@repo/ui/src/components/ui/button';
import { Input } from '@repo/ui/src/components/ui/input';
import { Label } from '@repo/ui/src/components/ui/label';
import { Textarea } from '@repo/ui/src/components/ui/textarea';
import { cn } from '@repo/ui/src/lib/utils';
import { Check } from 'lucide-react';
import type { Invoice } from '@repo/shared-types';
import { formatVnd } from '../../../lib/formatVnd';
import { commitAmountInput, isSubmittableAmount } from '../../../lib/paymentAmountInput';
import { MANUAL_SOURCE_OPTIONS, type ManualSource } from '../../../lib/paymentSource';
import { useClientOptions } from '../../../hooks/useClients';
import { useRecordPayment } from '../../../hooks/usePayments';
import { DatePicker } from '../../../components/pickers/DatePicker';
import { BusyButton } from '../../../components/BusyButton';

type Source = ManualSource;

type Mode =
  | { kind: 'invoice'; invoice: Pick<Invoice, 'id' | 'invoiceCode'>; defaultAmount: number }
  | { kind: 'adhoc' };

interface RecordPaymentDrawerProps {
  open: boolean;
  mode: Mode;
  onOpenChange: (open: boolean) => void;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function RecordPaymentDrawer({ open, mode, onOpenChange }: RecordPaymentDrawerProps) {
  const { t } = useTranslation('payments');
  const formKey = open ? (mode.kind === 'invoice' ? `inv-${mode.invoice.id}` : 'adhoc') : 'closed';
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{t('drawer.title')}</SheetTitle>
          <SheetDescription>
            {mode.kind === 'invoice'
              ? t('drawer.subtitle.invoice', { code: mode.invoice.invoiceCode })
              : t('drawer.subtitle.adhoc')}
          </SheetDescription>
        </SheetHeader>
        <RecordForm key={formKey} mode={mode} onOpenChange={onOpenChange} />
      </SheetContent>
    </Sheet>
  );
}

function RecordForm({
  mode,
  onOpenChange,
}: {
  mode: Mode;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('payments');
  const defaultAmount = mode.kind === 'invoice' ? mode.defaultAmount : 0;

  const [amount, setAmount] = useState<number>(defaultAmount);
  // Invoice mode always prefills the balance owed, including exactly 0đ (a
  // fully-deducted invoice — see InvoiceDetailPage's defaultAmount) so it's
  // submittable without the teacher having to type anything. Adhoc mode has
  // no real default to show, so it starts blank like any unfilled field.
  const [amountInput, setAmountInput] = useState<string>(
    mode.kind === 'invoice' ? formatVnd(defaultAmount) : '',
  );
  const [date, setDate] = useState<string>(todayIso());
  // "đa số là người ta ck chứ không phải trả tiền mặt" (Johnny, design doc §4)
  // — most parents transfer, not cash-in-hand, so the default now matches
  // the common case instead of making them re-pick it every time.
  const [source, setSource] = useState<Source>('MANUAL_BANK');
  const [content, setContent] = useState<string>('');
  const [clientId, setClientId] = useState<string | null>(null);

  const { data: clients } = useClientOptions();
  const recordMutation = useRecordPayment();

  const isPending = recordMutation.isPending;
  // Zero is a valid payment (settles a fully-deducted 0đ invoice); a blank
  // field is not, even though `amount` itself defaults to 0 — see
  // isSubmittableAmount's doc comment.
  const canSubmit = isSubmittableAmount(amount, amountInput) && !!date && !isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const payload =
      mode.kind === 'invoice'
        ? {
            source,
            amount,
            transactionDate: new Date(`${date}T00:00:00.000Z`).toISOString(),
            content: content.trim(),
            invoiceId: mode.invoice.id,
          }
        : {
            source,
            amount,
            transactionDate: new Date(`${date}T00:00:00.000Z`).toISOString(),
            content: content.trim(),
            clientId: clientId || null,
          };
    recordMutation.mutate(payload, {
      onSuccess: (result) => {
        const status = result.invoiceStatus;
        if (status === 'PAID') toast.success(t('toast.paid'));
        else if (status === 'PARTIAL') toast.success(t('toast.partial'));
        else toast.success(t('toast.adhoc'));
        onOpenChange(false);
      },
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 py-4">
      <div className="space-y-2">
        <Label htmlFor="pay-amount">{t('drawer.field.amount')}</Label>
        <Input
          id="pay-amount"
          inputMode="numeric"
          value={amountInput}
          onChange={(e) => setAmountInput(e.target.value)}
          onBlur={() => {
            const { amount: n, display } = commitAmountInput(amountInput);
            setAmount(n);
            setAmountInput(display);
          }}
          placeholder={t('drawer.field.amountPlaceholder')}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="pay-date">{t('drawer.field.date')}</Label>
        <DatePicker id="pay-date" value={date} onChange={setDate} />
      </div>

      <div className="space-y-2">
        <Label>{t('drawer.field.source')}</Label>
        <div className="flex flex-wrap gap-2">
          {MANUAL_SOURCE_OPTIONS.map((opt) => {
            const isSelected = source === opt.value;
            const Icon = opt.icon;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSource(opt.value)}
                className={cn(
                  'inline-flex items-center gap-2 px-3 py-2 border text-sm font-medium transition-all rounded-md',
                  isSelected
                    ? 'border-brand-500 bg-brand-50 text-brand-700 ring-1 ring-brand-500'
                    : 'border-surface-200 text-surface-600 hover:border-brand-300 hover:bg-brand-50/50',
                )}
              >
                <Icon
                  className={cn(
                    'w-4 h-4',
                    isSelected ? 'text-brand-600' : 'text-surface-400',
                  )}
                  strokeWidth={1.8}
                />
                {t(`drawer.source.${opt.value}`)}
                {isSelected && (
                  <Check
                    className="w-3.5 h-3.5 text-brand-600"
                    strokeWidth={2.5}
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="pay-content">{t('drawer.field.content')}</Label>
        <Textarea
          id="pay-content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={t(`drawer.field.contentPlaceholder.${source}`)}
          rows={2}
        />
      </div>

      {mode.kind === 'adhoc' && (
        <div className="space-y-2">
          <Label htmlFor="pay-client">{t('drawer.field.client')}</Label>
          <select
            id="pay-client"
            value={clientId ?? ''}
            onChange={(e) => setClientId(e.target.value || null)}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
          >
            <option value="">{t('drawer.field.noClient')}</option>
            {(clients ?? []).map((cl) => (
              <option key={cl.id} value={cl.id}>
                {cl.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <SheetFooter className="flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4">
        <Button
          type="button"
          variant="ghost"
          onClick={() => onOpenChange(false)}
          disabled={isPending}
        >
          {t('drawer.cancel')}
        </Button>
        <BusyButton type="submit" disabled={!canSubmit} busy={isPending} busyLabel={t('drawer.saving')}>
          {t('drawer.save')}
        </BusyButton>
      </SheetFooter>
    </form>
  );
}
